import { useEffect, useMemo, useRef, useState } from "react";
import { IoClose, IoCloseOutline } from "react-icons/io5";
import PhoneInputField from "@/Common/PhoneInputField";
import DatePickerField from "@/Common/DatePickerField";
import LocationSearchPopover from "@/Component/Module/HolidayListing/LocationSearchPopover";
import LocationSearchScreen from "@/Component/Module/HolidayListing/LocationSearchScreen";
import { useAlert } from "@/Common/CustomAlert/AlertContext";
import { saveHomePageEnquiryPopup } from "@/services/api/common";
import { isPopupApiSuccess } from "@/utils/sitePopups";
import { getSearchDisplayLabel } from "@/utils/holidaySearch";
import { parsePhoneForApi, validatePhoneValue } from "@/utils/phoneInput";
import { withRegionPath } from "@/utils/session";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import useIsDesktop from "@/hooks/useIsDesktop";
import styles from "./HomePageEnquiryPopup.module.css";
import { isIndianUser } from "@/utils/getCountryCode";

const PROMO_AUTO_SLIDE_MS = 4000;
const PROMO_SLIDE_TRANSITION_MS = 550;

/** Per-slide hero image (same URL for now; replace per slide when assets are ready). */
const DEFAULT_PROMO_IMAGE = "/assets/images/HomeEnquiry.webp";

const PROMO_CONTENT = [
  {
    headline: "More Value for Every Journey",
    image: "/assets/images/HomeEnquiry1.webp",
    items: [
      {
        title: "Competitive Pricing ",
        text: "Competitive pricing on flights, hotels, and holidays, all in one place. ",
        icon: "price",
      },
      {
        title: "Exclusive Offers ",
        text: "Unlock special offers and seasonal deals curated specially for you.",
        icon: "discount",
      },
    ],
  },
  {
    headline: "Your Holidays in Your Way ",
    image: "/assets/images/HomeEnquiry2.webp",
    items: [
      {
        title: "Customised Holidays ",
        text: "From itinerary to stays, personalise your vacations your way. ",
        icon: "price",
      },
      {
        title: "Everything in One Place",
        text: "Plan your experiences without juggling multiple platforms.",
        icon: "discount",
      },
    ],
  },
  {
    headline: "With You Beyond the Booking",
    image: "/assets/images/HomeEnquiry3.webp",
    items: [
      {
        title: "Dedicated Travel Guide ",
        text: "Personalized guidance from experienced travel experts.",
        icon: "price",
      },
      {
        title: "End-to-End Support ",
        text: "Count on our team for assistance from planning to return.",
        icon: "discount",
      },
    ],
  },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CALL_TIME_OPTIONS = [
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "1:00 PM",
  "2:00 PM",
  "3:00 PM",
  "4:00 PM",
  "5:00 PM",
  "6:00 PM",
  "7:00 PM",
];

function PromoSlideContent({ slide }) {
  return (
    <>
      <h3 className={styles.promoTitle}>{slide.headline}</h3>
      <div className={styles.promoList}>
        {slide.items.map((item) => (
          <div key={item.title} className={styles.promoItem}>
            <span className={styles.promoIcon}>
              <PromoIcon icon={item.icon} />
            </span>
            <div>
              <p className={styles.promoItemTitle}>{item.title}</p>
              <p className={styles.promoItemText}>{item.text}</p>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function PromoIcon({ icon }) {
  if (icon === "discount") {
    return (
      <img
        src="/images/login-discount.svg"
        alt=""
        width={32}
        height={32}
        className={styles.promoIconImage}
      />
    );
  }

  return (<img
    src="/assets/icons/pricetag.svg"
    alt=""
    width={32}
    height={32}
    className={styles.promoIconImage}
  />
  )
}

function createFormState() {
  return {
    fullName: "",
    email: "",
    phone: "",
    destination: "",
    timeToCall: "",
    dateOfBirth: "",
  };
}

export default function HomePageEnquiryPopup({ open, onClose }) {
  const { showAlert } = useAlert();
  const destinationWrapRef = useRef(null);
  const promoMediaTrackRef = useRef(null);
  const promoPositionRef = useRef(1);
  const { isDesktop, mounted } = useIsDesktop(900);
  const [form, setForm] = useState(createFormState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [promoPositionIndex, setPromoPositionIndex] = useState(1);
  const [promoTransitionEnabled, setPromoTransitionEnabled] = useState(true);
  const [destinationPopoverOpen, setDestinationPopoverOpen] = useState(false);
  const [mobileDestinationSearchOpen, setMobileDestinationSearchOpen] = useState(false);

  const isMobileDestination = mounted && !isDesktop;

  const promoSlideCount = PROMO_CONTENT.length;
  const promoHasMultiple = promoSlideCount > 1;

  const promoLoopSlides = useMemo(() => {
    if (!promoHasMultiple) return PROMO_CONTENT;
    return [
      PROMO_CONTENT[promoSlideCount - 1],
      ...PROMO_CONTENT,
      PROMO_CONTENT[0],
    ];
  }, [promoHasMultiple, promoSlideCount]);

  const promoCloneFirstIndex = promoSlideCount + 1;
  promoPositionRef.current = promoPositionIndex;

  const promoActiveDotIndex = promoHasMultiple
    ? (promoPositionIndex - 1 + promoSlideCount) % promoSlideCount
    : 0;

  const promoTrackStyle =
    promoHasMultiple
      ? {
        transform: `translate3d(-${promoPositionIndex * 100}%, 0, 0)`,
        transition: promoTransitionEnabled
          ? `transform ${PROMO_SLIDE_TRANSITION_MS}ms ease`
          : "none",
      }
      : undefined;

  useBodyScrollLock(open || mobileDestinationSearchOpen);

  const currentPageUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return window.location.href;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    setForm(createFormState());
    setErrors({});
    setLoading(false);
    setPromoPositionIndex(promoHasMultiple ? 1 : 0);
    setPromoTransitionEnabled(true);
    setDestinationPopoverOpen(false);
    setMobileDestinationSearchOpen(false);

    return undefined;
  }, [open, promoHasMultiple]);

  useEffect(() => {
    if (!open || !promoHasMultiple) return undefined;

    const timer = window.setInterval(() => {
      setPromoTransitionEnabled(true);
      setPromoPositionIndex((prev) => prev + 1);
    }, PROMO_AUTO_SLIDE_MS);

    return () => window.clearInterval(timer);
  }, [open, promoHasMultiple]);

  useEffect(() => {
    if (promoTransitionEnabled) return undefined;

    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setPromoTransitionEnabled(true));
    });

    return () => window.cancelAnimationFrame(frame);
  }, [promoPositionIndex, promoTransitionEnabled]);

  const handlePromoTrackTransitionEnd = (event) => {
    if (!promoHasMultiple) return;
    if (event.propertyName !== "transform") return;
    if (event.target !== promoMediaTrackRef.current) return;

    if (promoPositionRef.current === promoCloneFirstIndex) {
      setPromoTransitionEnabled(false);
      setPromoPositionIndex(1);
      return;
    }

    if (promoPositionRef.current === 0) {
      setPromoTransitionEnabled(false);
      setPromoPositionIndex(promoSlideCount);
    }
  };

  const goToPromoSlide = (dotIndex) => {
    if (!promoHasMultiple) return;
    setPromoTransitionEnabled(true);
    setPromoPositionIndex(dotIndex + 1);
  };

  if (!open) return null;

  const handleDestinationSelect = (item) => {
    if (!item) return;

    setForm((prev) => ({
      ...prev,
      destination: getSearchDisplayLabel(item, item.label),
    }));
    setErrors((prev) => ({ ...prev, destination: "" }));
    setDestinationPopoverOpen(false);
    setMobileDestinationSearchOpen(false);
  };

  const openMobileDestinationSearch = () => {
    setDestinationPopoverOpen(false);
    setMobileDestinationSearchOpen(true);
  };

  const handleDestinationFocus = () => {
    if (isMobileDestination) {
      openMobileDestinationSearch();
      return;
    }
    setDestinationPopoverOpen(true);
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.fullName.trim()) nextErrors.fullName = "Full name is required";
    if (!form.email.trim()) nextErrors.email = "Email is required";
    else if (!EMAIL_RE.test(form.email.trim())) nextErrors.email = "Enter a valid email";

    const phoneError = validatePhoneValue(form.phone);
    if (phoneError) nextErrors.phone = phoneError;

    if (!form.destination.trim()) nextErrors.destination = "Destination is required";
    if (!form.timeToCall) nextErrors.timeToCall = "Select call time";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    const parsedPhone = parsePhoneForApi(form.phone);
    if (!parsedPhone?.phoneNo) {
      setErrors((prev) => ({ ...prev, phone: "Enter a valid mobile number" }));
      return;
    }

    setLoading(true);
    try {
      const response = await saveHomePageEnquiryPopup({
        email: form.email.trim(),
        fullName: form.fullName.trim(),
        countryCode: parsedPhone.countryCode,
        phone: parsedPhone.phoneNo,
        timeToCall: `${form.date} ${form.timeToCall}`,
        currentPageUrl,
        destination: form.destination.trim(),
      });

      if (!isPopupApiSuccess(response)) {
        throw new Error(response?.message || "Unable to submit enquiry.");
      }

      onClose?.();
      window.location.href = withRegionPath("/thank-you?context=enquiry");
    } catch (error) {
      showAlert({
        title: "Submission failed",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to submit enquiry. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.root} role="dialog" aria-modal="true" aria-labelledby="home-enquiry-title">
      <button type="button" className={styles.overlay} aria-label="Close enquiry popup" onClick={onClose} />

      <div className={styles.modal}>
        <aside className={styles.promoPane} aria-hidden="true">
          <div className={styles.promoMediaViewport}>
            <div
              ref={promoMediaTrackRef}
              className={styles.promoMediaTrack}
              style={promoTrackStyle}
              onTransitionEnd={handlePromoTrackTransitionEnd}
            >
              {promoLoopSlides.map((slide, index) => (
                <div
                  key={`${slide.headline} - media - ${index}`}
                  className={styles.promoMediaSlide}
                >
                  <div
                    className={styles.promoSlideBg}
                    style={{
                      backgroundImage: `url(${slide.image || DEFAULT_PROMO_IMAGE})`,
                    }}
                    aria-hidden="true"
                  />
                </div>
              ))}
            </div>
            <div className={styles.promoPaneOverlay} aria-hidden="true" />
          </div>

          <div className={styles.promoBottom}>
            <div className={styles.promoTextViewport}>
              <div className={styles.promoTextTrack} style={promoTrackStyle}>
                {promoLoopSlides.map((slide, index) => (
                  <div
                    key={`${slide.headline} - text - ${index}`}
                    className={styles.promoContent}
                  >
                    <PromoSlideContent slide={slide} />
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.dots}>
              {PROMO_CONTENT.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={
                    index === promoActiveDotIndex ? styles.dotActive : styles.dot
                  }
                  aria-label={`Show promo slide ${index + 1}`}
                  aria-current={index === promoActiveDotIndex ? "true" : undefined}
                  onClick={() => goToPromoSlide(index)}
                />
              ))}
            </div>
          </div>
        </aside>

        <div className={styles.formPane}>
          <div className={styles.formHeader}>
            <h2 id="home-enquiry-title" className={styles.formTitle}>
              Got a Question? Let's Connect!
            </h2>
            <button type="button" className={styles.closeBtn} aria-label="Close" onClick={onClose}>
              <IoCloseOutline size={22} />
            </button>
          </div>

          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.outlineField}>
              <input
                id="home-enquiry-name"
                name="fullName"
                className={`${styles.outlineControl} ${errors.fullName ? styles.inputError : ""} `}
                placeholder="Enter Your Full Name"
                value={form.fullName}
                onChange={(event) => {
                  setForm((prev) => ({ ...prev, fullName: event.target.value }));
                  setErrors((prev) => ({ ...prev, fullName: "" }));
                }}
              />
              <span className={styles.outlineLabel}>Full Name*</span>
              {errors.fullName ? <span className={styles.errorText}>{errors.fullName}</span> : null}
            </div>

            <div className={styles.outlineField}>
              <input
                id="home-enquiry-email"
                name="email"
                type="email"
                className={`${styles.outlineControl} ${errors.email ? styles.inputError : ""} `}
                placeholder="Enter Your Email ID"
                value={form.email}
                onChange={(event) => {
                  setForm((prev) => ({ ...prev, email: event.target.value }));
                  setErrors((prev) => ({ ...prev, email: "" }));
                }}
              />
              <span className={styles.outlineLabel}>Email ID*</span>
              {errors.email ? <span className={styles.errorText}>{errors.email}</span> : null}
            </div>

            <div
              className={`${styles.outlineField} ${styles.phoneOutlineField} ${errors.phone ? styles.error : ""
                } `.trim()}
            >
              <PhoneInputField
                id="home-enquiry-phone"
                value={form.phone}
                onChange={(value) => {
                  setForm((prev) => ({ ...prev, phone: value || "" }));
                  setErrors((prev) => ({ ...prev, phone: "" }));
                }}
                error={Boolean(errors.phone)}
                placeholder="Enter Your Phone Number"
              />
              <span className={styles.outlineLabel}>Phone Number*</span>
              {errors.phone ? <span className={styles.errorText}>{errors.phone}</span> : null}
            </div>

            <div
              className={`${styles.outlineField} ${destinationPopoverOpen ? styles.destinationFieldActive : ""
                } `.trim()}
            >
              <div className={styles.destinationWrap} ref={destinationWrapRef}>
                <input
                  id="home-enquiry-destination"
                  name="destination"
                  className={`${styles.outlineControl} ${errors.destination ? styles.inputError : ""} `}
                  placeholder="Enter Destination"
                  value={form.destination}
                  autoComplete="off"
                  readOnly={isMobileDestination}
                  aria-expanded={destinationPopoverOpen || mobileDestinationSearchOpen}
                  aria-haspopup="listbox"
                  onFocus={handleDestinationFocus}
                  onClick={isMobileDestination ? openMobileDestinationSearch : undefined}
                  onChange={
                    isMobileDestination
                      ? undefined
                      : (event) => {
                        setForm((prev) => ({
                          ...prev,
                          destination: event.target.value,
                        }));
                        setErrors((prev) => ({ ...prev, destination: "" }));
                        setDestinationPopoverOpen(true);
                      }
                  }
                />
                {mounted && isDesktop && destinationPopoverOpen ? (
                  <LocationSearchPopover
                    anchorRef={destinationWrapRef}
                    query={form.destination}
                    onClose={() => setDestinationPopoverOpen(false)}
                    onSelect={handleDestinationSelect}
                    className={styles.enquiryDestinationPopover}
                    className2={styles.locationPopoverBody}
                  />
                ) : null}
              </div>
              <span className={styles.outlineLabel}>Destination*</span>
              {errors.destination ? (
                <span className={styles.errorText}>{errors.destination}</span>
              ) : null}
            </div>

            <p className={styles.sectionLabel}>Best time to call</p>

            <div className={styles.row2}>
              <div className={styles.outlineField}>
                <select
                  id="home-enquiry-time"
                  name="timeToCall"
                  className={`${styles.outlineControl} ${styles.outlineSelect} ${errors.timeToCall ? styles.inputError : ""
                    } `}
                  value={form.timeToCall}
                  onChange={(event) => {
                    setForm((prev) => ({ ...prev, timeToCall: event.target.value }));
                    setErrors((prev) => ({ ...prev, timeToCall: "" }));
                  }}
                >
                  <option value="">Select</option>
                  {CALL_TIME_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <span className={styles.outlineLabel}>Select Time*</span>
                {errors.timeToCall ? (
                  <span className={styles.errorText}>{errors.timeToCall}</span>
                ) : null}
              </div>

              <DatePickerField
                value={form.date}
                onChange={(value) => setForm((prev) => ({ ...prev, date: value }))}
                label="Select Date"
                minDate={new Date().toISOString().slice(0, 10)}
                wrapperClassName={styles.dateField}
              />
            </div>

            <button type="submit" className={styles.submitBtn} disabled={loading}>
              {loading ? "Submitting..." : "Talk To Us"}
            </button>
          </form>
          {!isIndianUser() ?
            <div className={styles.tollfree}>
              <span>24x7 Toll Free Support:</span>
              <span className={styles.callBtn}>+971 800500777</span>
              <a href="tel:+971800500777" className={styles.callBtnMobile}>
                <span>+971 800 500777</span>
              </a>
            </div>
            :
            <div className={styles.tollfree}>
              <span>24x7 Support:</span>
              <span className={styles.callBtn}>+91 96100 06398</span>
              <a href="tel:+919610006398" className={styles.callBtnMobile}>
                <span>+91 96100 06398</span>
              </a>
            </div>
          }
        </div>
      </div>

      {isMobileDestination && mobileDestinationSearchOpen ? (
        <div className={styles.mobileDestinationSearchLayer}>
          <LocationSearchScreen
            initialQuery={form.destination}
            onClose={() => setMobileDestinationSearchOpen(false)}
            onSelect={handleDestinationSelect}
          />
        </div>
      ) : null}
    </div>
  );
}
